import { env } from "@/lib/env";
import * as Sentry from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { PageNotFound } from "./components/PageNotFound/PageNotFound";
import { CORRELATION_ID } from "./lib/constants";
import "./styles/global.scss";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultNotFoundComponent: PageNotFound,
  defaultPreload: false,
  defaultStructuralSharing: true,
  scrollRestoration: true,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

Sentry.init({
  release: env.COMMIT_SHA,
  dsn: "https://e17f27473f565c688f7601c98abd93b3@o4509196409438208.ingest.de.sentry.io/4509196435325008",
  environment: env.RUN_ENV,
  enabled: env.NODE_ENV === "production",

  // Add optional integrations for additional features
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router), Sentry.replayIntegration()],

  tracePropagationTargets:
    env.NODE_ENV !== "production"
      ? [
          // Since the API is on a separate port in development we must explicitly allow it
          /.+\.terralux.test/,
        ]
      : undefined,

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 1,

  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: env.NODE_ENV === "production" ? 0.1 : 1,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  initialScope: {
    tags: {
      // Sentry automatically includes a "sentry-trace" header to our outgoing API requsts,
      // but we want a separate correlation id we can match to our API logs.
      correlation_id: CORRELATION_ID,
    },
  },
});

export function App() {
  return <RouterProvider router={router} />;
}
